import { Component, HostBinding, OnDestroy, OnInit, Renderer2, ViewChild, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppFooter } from './app.footer';
import { LayoutService, TabCloseEvent } from '@/layout/service/layout.service';
import { AppConfigurator } from './app.configurator';
import {AppSidebar} from "./app.sidebar";
import { BackgroundTileService } from '../../services/background-tile.service';
import { TrpcService } from '../../services/trpc.service';
import { ReconciliationAlert, ReconciliationAlertService } from '../../services/reconciliation-alert.service';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppFooter, AppConfigurator],
    template: `
        @if (dateOverrideActive) {
            <div style="background: #f97316; color: white; text-align: center; padding: 6px 12px; font-weight: 600; font-size: 13px; position: fixed; top: 0; left: 0; right: 0; z-index: 9999; height: 32px; box-sizing: border-box;">
                Date Override Active: Site is operating as {{ dateOverrideLabel }}
            </div>
        }
        @if (reconciliationAlert && reconciliationAlert.needsAlert) {
            <div [style.top.px]="dateOverrideActive ? 32 : 0"
                 style="background: #dc2626; color: white; text-align: center; padding: 6px 12px; font-weight: 600; font-size: 13px; position: fixed; left: 0; right: 0; z-index: 9999; cursor: pointer; height: 32px; box-sizing: border-box;"
                 (click)="goToSystemStatus()">
                {{ reconciliationBannerText() }}
                <span style="text-decoration: underline; margin-left: 8px;">View details &rarr;</span>
            </div>
        }
        <div class="layout-container" [ngClass]="containerClass">
            <div app-topbar></div>
            <div app-sidebar></div>
            <div class="layout-content-wrapper">
                <div class="layout-content" [ngStyle]="backgroundStyle()">
                    <div class="layout-content-inner">
                        <router-outlet></router-outlet>
                        <div app-footer></div>
                    </div>
                </div>
            </div>
        </div>
        <app-configurator />
    `,
})
export class AppLayout implements OnInit, OnDestroy {
    dateOverrideActive = false;
    dateOverrideLabel = '';

    // Reconciliation alert (banner). Populated via reconciliationAlertService.alert$.
    reconciliationAlert: ReconciliationAlert | null = null;
    reconciliationAlertSubscription?: Subscription;

    /**
     * Sets the --banner-offset CSS custom property on the host element, which
     * the layout SCSS reads to shift the fixed topbar + sidebar + content
     * origin down by the combined banner height. 32px per active banner.
     * Without this the fixed topbar sits at top: 0 and gets covered by the
     * fixed banners at the same position, hiding the topbar behind them.
     */
    @HostBinding('style.--banner-offset')
    get bannerOffset(): string {
        const count = (this.dateOverrideActive ? 1 : 0) +
            (this.reconciliationAlert?.needsAlert ? 1 : 0);
        return `${count * 32}px`;
    }

    overlayMenuOpenSubscription: Subscription;

    tabOpenSubscription: Subscription;

    tabCloseSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopbar!: AppTopbar;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private backgroundTileService: BackgroundTileService,
        private trpcService: TrpcService,
        private reconciliationAlertService: ReconciliationAlertService
    ) {
        this.reconciliationAlertSubscription = this.reconciliationAlertService.alert$.subscribe((alert) => {
            this.reconciliationAlert = alert;
        });
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        this.appSidebar.appMenu.el.nativeElement.isSameNode(event.target) ||
                        this.appSidebar.appMenu.el.nativeElement.contains(event.target) ||
                        this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if ((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appSidebar.appMenu.menuContainer.nativeElement, 'scroll', (event) => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });

        this.tabOpenSubscription = this.layoutService.tabOpen$.subscribe((tab) => {
            this.router.navigate(tab.routerLink);
            this.layoutService.openTab(tab);
        });

        this.tabCloseSubscription = this.layoutService.tabClose$.subscribe((event: TabCloseEvent) => {
            if (this.router.isActive(event.tab.routerLink[0], { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' })) {
                const tabs = this.layoutService.tabs;

                if (tabs.length > 1) {
                    if (event.index === tabs.length - 1) this.router.navigate(tabs[tabs.length - 2].routerLink);
                    else this.router.navigate(tabs[event.index + 1].routerLink);
                } else {
                    this.router.navigate(['/']);
                }
            }

            this.layoutService.closeTab(event.index);
        });
    }

    async ngOnInit() {
        try {
            const result = await (this.trpcService as any).developer.getEffectiveDate.query();
            this.dateOverrideActive = result.isOverridden;
            if (result.isOverridden) {
                const d = new Date(result.effectiveDate);
                this.dateOverrideLabel = d.toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                });
            }
        } catch {
            // Not logged in or endpoint unavailable — silently ignore
        }
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        this.layoutService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }

        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }

        this.unblockBodyScroll();
    }

    get containerClass() {
        return {
            'layout-slim': this.layoutService.layoutConfig().menuMode === 'slim',
            'layout-slim-plus': this.layoutService.layoutConfig().menuMode === 'slim-plus',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-light': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && !this.layoutService.isDarkTheme(),
            'layout-dark': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && this.layoutService.isDarkTheme(),
            'layout-primary': !this.layoutService.isDarkTheme() && this.layoutService.layoutConfig().layoutTheme === 'primaryColor'
        };
    }

    backgroundStyle = computed(() => ({
        'background-color': 'var(--surface-ground)',
        'background-image': `url(${this.backgroundTileService.backgroundUrl()})`,
        'background-repeat': 'repeat',
        'background-size': '400px 400px',
        'background-position': 'center'
    }));

    /** Copy for the reconciliation banner — driven off the alert reason. */
    reconciliationBannerText(): string {
        const alert = this.reconciliationAlert;
        if (!alert || !alert.needsAlert) return '';
        const when = alert.lastRunAt ? new Date(alert.lastRunAt).toLocaleString() : 'never';
        switch (alert.reason) {
            case 'discrepancies':
                return `${alert.flaggedCount} tenant${alert.flaggedCount === 1 ? '' : 's'} failed reconciliation on ${when}.`;
            case 'stale':
                return `Reconciliation audit hasn't run since ${when}.`;
            case 'failed':
                return `Last reconciliation audit failed on ${when}.`;
            case 'never-run':
                return 'Reconciliation audit has never run.';
            default:
                return 'Reconciliation audit needs attention.';
        }
    }

    /** Click handler for the banner — sends the admin to the system-status page. */
    goToSystemStatus() {
        this.router.navigate(['/admin/system-status']);
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }

        if (this.tabOpenSubscription) {
            this.tabOpenSubscription.unsubscribe();
        }

        if (this.tabCloseSubscription) {
            this.tabCloseSubscription.unsubscribe();
        }

        if (this.reconciliationAlertSubscription) {
            this.reconciliationAlertSubscription.unsubscribe();
        }
    }
}
